/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';
import { isTopbarMenuPageActive } from '../currentPageMatch';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';
import {
  createSlug,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
} from '../../../../util/urlHelpers';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const transactionTab =
      route?.params?.tab ||
      (page === 'SalesPage' || page === 'SaleDetailsPage'
        ? 'sales'
        : page === 'OrdersPage' || page === 'OrderDetailsPage'
          ? 'orders'
          : null);
    const isTransactionsPage = tab =>
      hasPageName('TransactionsPage') && currentPage === `TransactionsPage:${tab}`;
    const isCurrentPage = currentPage === page;

    return (
      isCMSPage(route?.params?.pageId) || isTransactionsPage(transactionTab) || isCurrentPage
    )
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    notificationCount = 0,
    customLinks,
    viewport,
    onLogout,
    showCreateListingsLink,
    showSellerVerificationLink,
    showAdminVerificationLink,
    isSellerVerificationApproved,
    createListingTarget,
  } = props;

  const user = ensureCurrentUser(currentUser);
  const currentPageClass = page => {
    return isTopbarMenuPageActive(currentPage, page) ? css.currentPage : null;
  };

  const extraLinks = [
    ...customLinks.map((linkConfig, index) => {
      return (
        <CustomLinkComponent
          key={`${linkConfig.text}_${index}`}
          linkConfig={linkConfig}
          currentPage={currentPage}
        />
      );
    }),
    <NamedLink
      key="contact"
      className={classNames(css.navigationLink, currentPageClass('ContactPage'))}
      name="ContactPage"
    >
      <FormattedMessage id="TopbarDesktop.nav.contactUs" />
    </NamedLink>,
  ];

  const createListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink
      className={css.createNewListingLink}
      name={createListingTarget?.name || 'NewListingPage'}
      params={createListingTarget?.params}
      to={createListingTarget?.to}
    >
      <FormattedMessage id="TopbarMobileMenu.newListingLink" />
    </NamedLink>
  ) : null;

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <nav className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          <div className={css.customLinksWrapper}>{extraLinks}</div>
          <div className={css.spacer} />
        </div>
        <div className={css.footer}>{createListingsLinkMaybe}</div>
      </nav>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;

  const manageListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink
      className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
      name="ManageListingsPage"
    >
      <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
    </NamedLink>
  ) : null;

  const sellerVerificationLinkMaybe = showSellerVerificationLink ? (
    <NamedLink
      className={classNames(
        css.navigationLink,
        css.navigationLinkWithBadge,
        currentPageClass('SellerVerificationPage')
      )}
      name="SellerVerificationPage"
    >
      <span>Seller Verification</span>
      {isSellerVerificationApproved ? <span className={css.statusBadge}>Approved</span> : null}
    </NamedLink>
  ) : null;

  const adminVerificationLinkMaybe = showAdminVerificationLink ? (
    <NamedLink
      className={classNames(css.navigationLink, currentPageClass('AdminVerificationPage'))}
      name="AdminVerificationPage"
    >
      Admin Verification
    </NamedLink>
  ) : null;

  const authorListingId = currentUser?.attributes?.profile?.publicData?.authorListingId || '';
  const title = currentUser?.attributes?.profile?.displayName;
  const slug = title && createSlug(title);
  const isChefProfilePublished = currentUser?.attributes?.profile?.publicData?.isPublished;
  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <div className={css.accountLinksWrapper}>
          <NamedLink
            className={classNames(css.inbox, currentPageClass(`TransactionsPage:${inboxTab}`))}
            name={inboxTab === 'sales' ? 'SalesPage' : 'OrdersPage'}
          >
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
          </NamedLink>
          {/* {manageListingsLinkMaybe} */}
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>
          {sellerVerificationLinkMaybe}
          {adminVerificationLinkMaybe}
        </div>
        <div className={css.customLinksWrapper}>{extraLinks}</div>
        <div className={css.spacer} />
      </div>
      <div className={css.footer}>{createListingsLinkMaybe}</div>
    </div>
  );
};

export default TopbarMobileMenu;
